import { c as _c } from "react/compiler-runtime";
import { useRef, useEffect, useState, useCallback, type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';
import { sumBy, filter, sort, map, pipe } from 'remeda';
import { useThrottledFn } from './hooks/useOnDebounce';
import { twMerge } from 'tailwind-merge';
import { useIntersection } from '@mantine/hooks';
const findRelevantStickyElements = (element: Element): Element[] => {
  const stickyElements: Element[] = [];
  let currentElement: Element | null = element;
  while (currentElement) {
    const parent: Element | null = currentElement.parentElement;
    if (!parent) break;
    Array.from(parent.children).forEach((child: Element) => {
      if (child.classList.contains('sticky') && child != element) {
        stickyElements.push(child);
      }
    });
    currentElement = parent;
  }
  return stickyElements;
};
const {
  debug: consoleDebug
} = console;
const getStickyElementsAbove = (element: Element, {
  debugName
}: {
  debugName?: string;
} = {}): Element[] => {
  const relevantStickies = findRelevantStickyElements(element).map(el => ({
    el,
    rect: el.getBoundingClientRect()
  }));
  const elementRect = element.getBoundingClientRect();
  const elementsAbove = relevantStickies.filter(s => s.el != element && !!(s.el.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING)).map(s => s.el);
  if (debugName) consoleDebug('elementsAbove', debugName, {
    relevantStickies,
    elementsAbove,
    elementRect
  });
  return elementsAbove;
};

/** automatically adjust tops so that each sticky stacks */
export function useSticky(options) {
  const $ = _c(23);
  let t0;
  if ($[0] !== options) {
    t0 = options ?? {};
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    disabled,
    debugName
  } = t0;
  const elRef = useRef(null);
  const [topOffset, setTopOffset] = useState(0);
  const t1 = `-${topOffset}px 0px 0px 0px`;
  let t2;
  if ($[2] !== t1) {
    t2 = {
      rootMargin: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    ref: sentinelRef,
    entry
  } = useIntersection(t2);
  const isSticking = (entry && !entry.isIntersecting) ?? false;
  let t3;
  if ($[4] !== debugName) {
    t3 = () => {
      const el = elRef.current;
      if (!el) {
        return setTopOffset(0);
      }
      const aboveElements = getStickyElementsAbove(el, {
        debugName
      });
      const totalHeight = sumBy(aboveElements, _temp);
      setTopOffset(totalHeight);
      if (debugName) {
        console.log(debugName, "sticky", {
          props: {
            top: `${totalHeight}px`
          },
          aboveElements
        });
      }
    };
    $[4] = debugName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const calculateProps = t3;
  const debouncedCalculateProps = useThrottledFn(calculateProps, 100);
  let t4;
  let t5;
  if ($[6] !== calculateProps || $[7] !== debouncedCalculateProps || $[8] !== disabled) {
    t4 = () => {
      if (disabled) {
        return;
      }
      const el_1 = elRef.current;
      if (!el_1) {
        return;
      }
      calculateProps();
      const mutationObserver = new MutationObserver(debouncedCalculateProps);
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style", "class"]
      });
      const relevantStickies = findRelevantStickyElements(el_1);
      const resizeObserver = new ResizeObserver(debouncedCalculateProps);
      relevantStickies.forEach(element => resizeObserver.observe(element));
      const handleScroll = debouncedCalculateProps;
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t5 = [calculateProps, debouncedCalculateProps, disabled];
    $[6] = calculateProps;
    $[7] = debouncedCalculateProps;
    $[8] = disabled;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      height: 0,
      visibility: "hidden"
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = disabled ? "" as const : "sticky" as const;
  let t8;
  if ($[12] !== sentinelRef || $[13] !== t7) {
    t8 = {
      ref: sentinelRef,
      style: t6 as CSSProperties,
      className: t7
    };
    $[12] = sentinelRef;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== disabled || $[16] !== topOffset) {
    t9 = disabled ? {
      top: undefined
    } : {
      top: `${topOffset}px`,
      zIndex: 5
    };
    $[15] = disabled;
    $[16] = topOffset;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const t10 = disabled ? "" as const : "sticky" as const;
  let t11;
  if ($[18] !== isSticking || $[19] !== t10 || $[20] !== t8 || $[21] !== t9) {
    t11 = {
      ref: elRef,
      sentinelProps: t8,
      style: t9,
      className: t10,
      isSticking
    };
    $[18] = isSticking;
    $[19] = t10;
    $[20] = t8;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}
function _temp(el_0) {
  return el_0.getBoundingClientRect().height;
}
type StickyRenderProps = {
  isSticking: boolean;
  stickyAttrs: {
    'data-sticking': boolean;
  };
};
type StickyProps = {
  children: ReactNode | ((props: StickyRenderProps) => ReactNode);
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
  debugName?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'className' | 'children'>;

/**
 * A component that makes its children sticky and provides styling hooks for when it is sticking.
 *
 * It passes a `data-sticking` attribute to the root element, and always adds the `group` class for Tailwind parent queries.
 *
 * @example
 * ```tsx
 * <Sticky>
 *   <div className="p-4 bg-gray-200 group-data-[sticking=true]:bg-green-200">
 *     I am a sticky header
 *   </div>
 * </Sticky>
 * ```
 *
 * It also provides a render prop for more complex scenarios.
 * @example
 * ```tsx
 * <Sticky>
 *   {({ isSticking, stickyAttrs }) => (
 *     <div {...stickyAttrs} className="p-4 bg-gray-200 group-data-[sticking=true]:bg-green-200">
 *       I am a sticky header.
 *       {isSticking && <span> (Sticking!)</span>}
 *     </div>
 *   )}
 * </Sticky>
 * ```
 */
export const Sticky = t0 => {
  const $ = _c(34);
  let children;
  let className;
  let debugName;
  let disabled;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      children,
      className,
      style,
      disabled,
      debugName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = debugName;
    $[4] = disabled;
    $[5] = props;
    $[6] = style;
  } else {
    children = $[1];
    className = $[2];
    debugName = $[3];
    disabled = $[4];
    props = $[5];
    style = $[6];
  }
  let t1;
  if ($[7] !== debugName || $[8] !== disabled) {
    t1 = {
      disabled,
      debugName
    };
    $[7] = debugName;
    $[8] = disabled;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const {
    ref,
    sentinelProps,
    style: stickyStyle,
    className: stickyClassName,
    isSticking
  } = useSticky(t1);
  let t2;
  if ($[10] !== sentinelProps) {
    t2 = <div {...sentinelProps} />;
    $[10] = sentinelProps;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== stickyStyle || $[13] !== style) {
    t3 = {
      ...stickyStyle,
      ...style
    };
    $[12] = stickyStyle;
    $[13] = style;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const t4 = !disabled && "group";
  let t5;
  if ($[15] !== className || $[16] !== stickyClassName || $[17] !== t4) {
    t5 = twMerge("sticky", stickyClassName, className, t4);
    $[15] = className;
    $[16] = stickyClassName;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== isSticking) {
    t6 = {
      "data-sticking": isSticking
    };
    $[19] = isSticking;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== children || $[22] !== isSticking) {
    t7 = typeof children == "function" ? children({
      isSticking,
      stickyAttrs: {
        "data-sticking": isSticking
      }
    }) : children;
    $[21] = children;
    $[22] = isSticking;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== props || $[25] !== ref || $[26] !== t3 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7) {
    t8 = <div ref={ref} style={t3} className={t5} {...props} {...t6}>{t7}</div>;
    $[24] = props;
    $[25] = ref;
    $[26] = t3;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== t2 || $[32] !== t8) {
    t9 = <>{t2}{t8}</>;
    $[31] = t2;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
};